import {AxiosInstance} from "axios";
import {
    AbsenceStatus,
    ContractType,
    JobRole,
    StaffingTarget,
    TeamAbsence,
    TeamHourBalances,
    TeamHourMovement,
    TeamMemberSchedule,
    TeamShiftException
} from "@/core/interfaces/TeamPlanning";

export interface PlanningProfileUpdateRequest {
    job_role?: JobRole | null;
    contract_type?: ContractType | null;
    weekly_hours?: number | null;
    is_interim?: boolean;
    skills?: string[];
    referent_laboratory_ids?: number[];
    appointment_type_ids?: number[];
}

export interface ShiftInput {
    week_index: number;
    day_of_week: number;
    starts_at: string;
    ends_at: string;
}

export interface ShiftsSyncRequest {
    rotation_weeks?: number;
    shifts: ShiftInput[];
}

export interface AbsenceFilters {
    status?: AbsenceStatus;
    from?: string;
    to?: string;
    /** Y-m-d, keeps the absences already over on that date: the archive. */
    ends_before?: string;
}

/**
 * `status` is deliberately absent: the server grants an absence on creation
 * and reserves the status flow to the pharmacy holders, so sending it from
 * the counter would fail the whole request.
 *
 * @property {string} starts_at - Date, Y-m-d.
 * @property {string} ends_at - Date, Y-m-d.
 */
export interface AbsenceStoreRequest {
    user_access_point_id: number;
    starts_at: string;
    ends_at: string;
    reason: string;
}

/** The member is immutable, and the server wants both dates or neither. */
export type AbsenceUpdateRequest =
    Partial<Pick<AbsenceStoreRequest, 'reason'>>
    & ({ starts_at: string; ends_at: string } | { starts_at?: never; ends_at?: never });

export interface AbsencePageFilters extends AbsenceFilters {
    /** Page size. Leaving it out is what makes the endpoint answer everything. */
    limit: number;
    page?: number;
    sort?: 'asc' | 'desc';
}

export interface AbsencePage {
    items: TeamAbsence[];
    total: number;
    lastPage: number;
}

export interface ShiftExceptionStoreRequest {
    user_access_point_id: number;
    date: string;
    reason: string;
    note?: string | null;
    slots?: { starts_at: string; ends_at: string }[] | null;
}

export type ShiftExceptionUpdateRequest = Partial<Pick<ShiftExceptionStoreRequest, 'reason' | 'note' | 'slots'>>;

export interface StaffingTargetInput {
    day_of_week: number;
    starts_at: string;
    ends_at: string;
    headcount: number;
}

/** A whole recurrence cycle, as the builder holds it. */
export interface ShiftGrid {
    rotation_weeks: number;
    shifts: ShiftInput[];
}

/** One message of the schedule thread, as the API replays it to the model. */
export interface ShiftSuggestionMessage {
    role: 'user' | 'assistant';
    content: string;
}

export interface ShiftSuggestionQuestion {
    text: string;
    /** Short answers the pharmacist can click, possibly none. */
    options: string[];
}

/**
 * The agent's answer to one turn of the thread: a complete grid, or the
 * questions it needs first — never both.
 */
export interface ShiftSuggestion {
    reply: string;
    questions: ShiftSuggestionQuestion[];
    proposal: ShiftGrid | null;
    /** French sentences: the agent's assumptions and the ranges outside the opening hours. */
    warnings: string[];
}

export interface HourMovementStoreRequest {
    user_access_point_id: number;
    date: string;
    delta_minutes: number;
    reason: string;
    note?: string | null;
}

export type HourMovementUpdateRequest = Partial<Pick<HourMovementStoreRequest, 'date' | 'delta_minutes' | 'reason' | 'note'>>;

export interface HourMovementFilters {
    user_access_point_id?: number;
    from?: string;
    to?: string;
}

/**
 * The team planning resource: member profiles, recurring shifts and
 * absences, keyed on the access point (api-v2, package TeamPlanning).
 */
const TeamPlanningResource = (client: AxiosInstance) => {
    return {
        /**
         * The planning profiles of the organization, with their recurring
         * shifts. Members without a saved profile yet are absent.
         */
        async schedules(organizationId: number): Promise<TeamMemberSchedule[]> {
            const response = await client.get(`organizations/${organizationId}/team-schedule`);

            return response.data.data;
        },

        /**
         * Upsert a member's planning profile, sending only what changed.
         */
        async updateProfile(
            organizationId: number,
            accessId: number,
            request: PlanningProfileUpdateRequest
        ): Promise<TeamMemberSchedule> {
            const response = await client.put(
                `organizations/${organizationId}/team-members/${accessId}/planning-profile`,
                {data: request}
            );

            return response.data.data;
        },

        /**
         * One turn of the schedule thread: the whole thread, kept by the
         * caller, and the grid as it stands in the builder. Nothing is
         * persisted: the caller reviews a proposal, then saves through
         * syncShifts.
         */
        async suggestShifts(
            organizationId: number,
            accessId: number,
            messages: ShiftSuggestionMessage[],
            grid: ShiftGrid
        ): Promise<ShiftSuggestion> {
            const response = await client.post(
                `organizations/${organizationId}/team-members/${accessId}/shifts/suggestion`,
                {data: {messages, grid}}
            );

            return response.data.data;
        },

        /**
         * Replace a member's whole recurrence grid at once.
         */
        async syncShifts(
            organizationId: number,
            accessId: number,
            request: ShiftsSyncRequest
        ): Promise<TeamMemberSchedule> {
            const response = await client.put(
                `organizations/${organizationId}/team-members/${accessId}/shifts`,
                {data: request}
            );

            return response.data.data;
        },

        /**
         * The absences of the organization, optionally filtered by status
         * and by overlap with a date range.
         */
        async absences(organizationId: number, filters: AbsenceFilters = {}): Promise<TeamAbsence[]> {
            const response = await client.get(`organizations/${organizationId}/team-absences`, {params: filters});

            return response.data.data;
        },

        /**
         * One page of absences, newest first when asked. Used by the archive,
         * the only surface whose volume grows without bound.
         */
        async absencesPage(organizationId: number, filters: AbsencePageFilters): Promise<AbsencePage> {
            const response = await client.get(`organizations/${organizationId}/team-absences`, {params: filters});
            const items: TeamAbsence[] = response.data.data;
            const meta = response.data.meta;

            // The renderer ships ahead of the server: until the paginated
            // branch is deployed the endpoint answers the whole collection.
            return meta
                ? {items, total: meta.total, lastPage: meta.last_page}
                : {items, total: items.length, lastPage: 1};
        },

        /**
         * Create an absence for a member. It is granted straight away.
         */
        async createAbsence(organizationId: number, request: AbsenceStoreRequest): Promise<TeamAbsence> {
            const response = await client.post(`organizations/${organizationId}/team-absences`, {data: request});

            return response.data.data;
        },

        /**
         * Update an absence; the member is immutable.
         */
        async updateAbsence(absenceId: number, request: AbsenceUpdateRequest): Promise<TeamAbsence> {
            const response = await client.patch(`team-absences/${absenceId}`, {data: request});

            return response.data.data;
        },

        async deleteAbsence(absenceId: number): Promise<void> {
            await client.delete(`team-absences/${absenceId}`);
        },

        /**
         * The dated shift overrides of the organization on a date range.
         */
        async shiftExceptions(organizationId: number, filters: { from?: string; to?: string } = {}): Promise<TeamShiftException[]> {
            const response = await client.get(`organizations/${organizationId}/team-shift-exceptions`, {params: filters});

            return response.data.data;
        },

        /**
         * Create a dated override of a member's day.
         */
        async createShiftException(organizationId: number, request: ShiftExceptionStoreRequest): Promise<TeamShiftException> {
            const response = await client.post(`organizations/${organizationId}/team-shift-exceptions`, {data: request});

            return response.data.data;
        },

        /**
         * Update a dated override; the member and date are immutable.
         */
        async updateShiftException(exceptionId: number, request: ShiftExceptionUpdateRequest): Promise<TeamShiftException> {
            const response = await client.patch(`team-shift-exceptions/${exceptionId}`, {data: request});

            return response.data.data;
        },

        /**
         * The staffing target grid of the organization.
         */
        async staffingTargets(organizationId: number): Promise<StaffingTarget[]> {
            const response = await client.get(`organizations/${organizationId}/staffing-targets`);

            return response.data.data;
        },

        /**
         * Replace the whole staffing target grid.
         */
        async syncStaffingTargets(organizationId: number, targets: StaffingTargetInput[]): Promise<StaffingTarget[]> {
            const response = await client.put(`organizations/${organizationId}/staffing-targets`, {data: {targets}});

            return response.data.data;
        },

        /**
         * The hour counter movements of the organization, newest first.
         */
        async hourMovements(organizationId: number, filters: HourMovementFilters = {}): Promise<TeamHourMovement[]> {
            const response = await client.get(`organizations/${organizationId}/team-hour-movements`, {params: filters});

            return response.data.data;
        },

        /**
         * Add a signed movement to the hour counter.
         */
        async createHourMovement(organizationId: number, request: HourMovementStoreRequest): Promise<TeamHourMovement> {
            const response = await client.post(`organizations/${organizationId}/team-hour-movements`, {data: request});

            return response.data.data;
        },

        /**
         * Edit a movement; the member is immutable.
         */
        async updateHourMovement(movementId: number, request: HourMovementUpdateRequest): Promise<TeamHourMovement> {
            const response = await client.patch(`team-hour-movements/${movementId}`, {data: request});

            return response.data.data;
        },

        async deleteHourMovement(movementId: number): Promise<void> {
            await client.delete(`team-hour-movements/${movementId}`);
        },

        /**
         * The counter synthesis: balances per member and team-wide.
         */
        async hourBalances(organizationId: number): Promise<TeamHourBalances> {
            const response = await client.get(`organizations/${organizationId}/team-hour-balances`);

            return response.data.data;
        }
    };
};

export default TeamPlanningResource;
